import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ZoomIn, ZoomOut, Loader2, Download, RefreshCcw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/utils";

/**
 * Admin-only modal that requests a 1h signed URL for a doctor's document
 * (cedula front/back, diploma, or photo) and renders it inline with zoom.
 * The link is minted per open, never cached, and only visible inside the
 * admin session — matches the LOPDP requirement of restricted access to
 * identification documents.
 */
const KIND_LABEL = {
  cedula_front: "Cédula – frente",
  cedula_back: "Cédula – reverso",
  diploma: "Título / certificado SENESCYT",
  photo: "Photo de profil",
};

export default function DocViewerDialog({ open, onOpenChange, doctorId, kind, doctorName }) {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(1);

  const fetchLink = async () => {
    if (!doctorId || !kind) return;
    setLoading(true);
    try {
      const r = await api.post(`/admin/doctors/${doctorId}/file-link`, null, { params: { kind } });
      setLink(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      setLink(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) { setZoom(1); setLink(null); fetchLink(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doctorId, kind]);

  // Try inline <img>; fall back to <iframe> if the browser fails to decode
  // (typical for PDFs — the backend serves Content-Disposition: attachment,
  // so <iframe> is the only reliable way to preview PDFs inline).
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [link?.url]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`doc-viewer-${kind}`} className="max-w-4xl w-[95vw] p-0 overflow-hidden">
        <DialogHeader className="px-5 py-3 border-b border-slate-200 bg-slate-50">
          <DialogTitle className="text-sm font-semibold text-slate-900 flex items-center justify-between">
            <span>{KIND_LABEL[kind] || kind} {doctorName ? `· ${doctorName}` : ""}</span>
            <div className="flex items-center gap-1">
              <Button data-testid="doc-zoom-out" size="icon" variant="ghost" className="h-8 w-8"
                      onClick={() => setZoom(z => Math.max(0.4, +(z - 0.25).toFixed(2)))}
                      disabled={imgFailed}><ZoomOut size={16}/></Button>
              <span className="text-xs text-slate-600 w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
              <Button data-testid="doc-zoom-in" size="icon" variant="ghost" className="h-8 w-8"
                      onClick={() => setZoom(z => Math.min(4, +(z + 0.25).toFixed(2)))}
                      disabled={imgFailed}><ZoomIn size={16}/></Button>
              <Button data-testid="doc-refresh" size="icon" variant="ghost" className="h-8 w-8"
                      onClick={fetchLink} title="Regénérer le lien"><RefreshCcw size={16}/></Button>
              {link?.url && (
                <a data-testid="doc-download" href={link.url} target="_blank" rel="noopener noreferrer"
                   className="ml-1 inline-flex items-center justify-center h-8 w-8 rounded hover:bg-slate-200 text-slate-700">
                  <Download size={16}/>
                </a>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="bg-slate-900/95 min-h-[60vh] max-h-[75vh] overflow-auto flex items-center justify-center p-4">
          {loading && <Loader2 className="text-white animate-spin" size={28}/>}
          {!loading && link?.url && !imgFailed && (
            <img data-testid="doc-image" src={link.url} alt={KIND_LABEL[kind] || kind}
                 onError={() => setImgFailed(true)}
                 style={{ transform: `scale(${zoom})`, transformOrigin: "center center",
                          transition: "transform 120ms ease-out", maxWidth: "100%", maxHeight: "70vh" }}
                 className="rounded shadow-2xl bg-white"/>
          )}
          {!loading && link?.url && imgFailed && (
            <iframe data-testid="doc-iframe" title={KIND_LABEL[kind] || kind}
                    src={link.url} className="w-full h-[70vh] bg-white rounded"/>
          )}
          {!loading && !link?.url && (
            <div className="text-slate-300 text-sm">Aucun document disponible.</div>
          )}
        </div>
        {link?.expires_at && (
          <div className="px-5 py-2 text-[11px] text-slate-500 border-t border-slate-200 bg-slate-50">
            Lien signé (HMAC · 1h) — expire {formatDateTime(new Date(link.expires_at * 1000))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
